
/**
 * `ScoreDetailModal` — modale che spiega **come è stato calcolato lo Score di
 * una specifica skill** per la persona corrente, a partire dai dati realmente
 * disponibili sulla riga (`AssessmentView`) e dalla configurazione di scoring
 * (`ScoringConfigDto`, pesi delle fonti/assi e `minScore`).
 *
 * Non si limita alla spiegazione generica del metodo (per quella c'è
 * {@link ScoreCalcGuide}): ricostruisce i **passaggi numerici** del backend
 * replicando le stesse formule di `api/src/domain/scoring.ts`:
 *  1. livello `1..5` di ciascuna Fonte di Livello presente (Self/AI esatti,
 *     Reviewer/Manager già continui);
 *  2. Livello_Aggregato = media pesata dei livelli delle fonti **presenti**,
 *     rinormalizzata sui pesi presenti;
 *  3. livello di Esperienza `1..5` dalla fascia dichiarata (assente ⇒ `1`);
 *  4. combinazione dei due assi (pesi `axisWeights`) normalizzati in `[0,1]`;
 *  5. rimappatura finale in `[minScore, 10]`.
 *
 * La Propensione (voto informativo) NON entra nel calcolo e non è mostrata qui.
 * I valori sono ricalcolati solo per **illustrare** il risultato dell'API: lo
 * Score autorevole resta quello di `row.score`, mostrato in evidenza.
 */
import { type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button, Modal } from './ui';
import {
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  LIVELLO_LABELS,
  LIVELLO_VALUES,
  livelloRank,
  type AssessmentView,
  type ExperienceRange,
  type Livello,
  type ScoringConfigDto,
} from '../shared/dto';

export interface ScoreDetailModalProps {
  /** Riga con i dati della skill (Livelli per fonte, esperienza, score). */
  row: AssessmentView;
  /** Configurazione di scoring (pesi fonti/assi, `minScore`). */
  config: ScoringConfigDto;
  /** Chiude il modale. */
  onClose: () => void;
}

/** Una fonte di Livello con il suo rango `1..5` e il peso applicato. */
interface SourceContribution {
  key: 'self' | 'ai' | 'reviewer' | 'manager';
  label: string;
  /** Descrizione della fonte (Livello esatto o media dei voti). */
  detail: string;
  rank: number;
  /** Peso di una singola occorrenza della fonte (peso di categoria). */
  weightPerRater: number;
  /** Numero di valutatori: per reviewer/manager ognuno pesa singolarmente. */
  count: number;
  /** Peso effettivo nella media pesata: `weightPerRater × count`. */
  weight: number;
}

/** Rango `1..5` di una fonte: Livello esatto → `livelloRank`, numero → identità. */
function sourceRank(level: Livello | number | null): number | null {
  if (level === null) return null;
  return typeof level === 'number' ? level : livelloRank(level);
}

/** Rango_Esperienza `1..5` (fascia assente ⇒ rango minimo `1`). */
function experienceRank(range: ExperienceRange | null): number {
  if (range === null) return 1;
  return EXPERIENCE_RANGE_VALUES.indexOf(range) + 1;
}

/** Formatta un numero con al più 2 decimali, senza zeri superflui. */
function fmt(value: number): string {
  return Number.parseFloat(value.toFixed(2)).toString();
}

/**
 * Descrizione del Livello per una fonte reviewer/manager: il Livello concordato
 * ("Livello e-X · Etichetta") quando tutti i valutatori hanno votato lo stesso
 * (`uniformLevel` valorizzato, anche con un solo valutatore), altrimenti
 * "Livelli multipli" (voti divergenti tra più valutatori).
 */
function raterLevelDetail(uniformLevel: Livello | null): string {
  if (uniformLevel === null) return 'Livelli multipli';
  return `Livello ${uniformLevel} · ${LIVELLO_LABELS[uniformLevel]}`;
}

export function ScoreDetailModal({ row, config, onClose }: ScoreDetailModalProps) {
  const { sourceWeights, axisWeights, minScore } = config;

  // Fonti di Livello presenti sulla riga, con rango e peso. L'ordine riflette
  // quello della guida (Self, AI, Reviewer, Manager).
  const contributions: SourceContribution[] = [];

  const selfRank = sourceRank(row.livelloSelf);
  if (selfRank !== null) {
    contributions.push({
      key: 'self',
      label: 'Self',
      detail: `Livello ${row.livelloSelf} · ${LIVELLO_LABELS[row.livelloSelf as Livello]}`,
      rank: selfRank,
      weightPerRater: sourceWeights.self,
      count: 1,
      weight: sourceWeights.self,
    });
  }

  const aiRank = sourceRank(row.aiLevel);
  if (aiRank !== null) {
    contributions.push({
      key: 'ai',
      label: 'AI',
      detail: `Livello ${row.aiLevel} · ${LIVELLO_LABELS[row.aiLevel as Livello]}`,
      rank: aiRank,
      weightPerRater: sourceWeights.ai,
      count: 1,
      weight: sourceWeights.ai,
    });
  }

  // Reviewer/Manager: ogni valutatore pesa singolarmente ⇒ peso effettivo =
  // peso di categoria × numero di valutatori (allineato al backend).
  // Il "Dato" mostra il Livello concordato quando tutti i valutatori hanno
  // votato lo stesso (`*UniformLevel`), altrimenti "Livelli multipli".
  const reviewerRank = sourceRank(row.reviewerLevelRank);
  if (reviewerRank !== null) {
    const count = Math.max(1, row.reviewerCount);
    contributions.push({
      key: 'reviewer',
      label: count === 1 ? 'Reviewer' : `Reviewer (${count})`,
      detail: raterLevelDetail(row.reviewerUniformLevel),
      rank: reviewerRank,
      weightPerRater: sourceWeights.reviewer,
      count,
      weight: sourceWeights.reviewer * count,
    });
  }

  const managerRank = sourceRank(row.managerLevelRank);
  if (managerRank !== null) {
    const count = Math.max(1, row.managerCount);
    contributions.push({
      key: 'manager',
      label: count === 1 ? 'Manager' : `Manager (${count})`,
      detail: raterLevelDetail(row.managerUniformLevel),
      rank: managerRank,
      weightPerRater: sourceWeights.manager,
      count,
      weight: sourceWeights.manager * count,
    });
  }

  const hasLevel = contributions.length > 0;

  // Livello_Aggregato: media pesata dei ranghi presenti (fallback a media
  // semplice se la somma dei pesi è 0). `null` se nessuna fonte.
  const weightSum = contributions.reduce((s, c) => s + c.weight, 0);
  const aggRank = !hasLevel
    ? null
    : weightSum === 0
      ? contributions.reduce((s, c) => s + c.rank, 0) / contributions.length
      : contributions.reduce((s, c) => s + c.weight * c.rank, 0) / weightSum;

  const expRank = experienceRank(row.yearsOfExperience);

  // Assi normalizzati in [0,1] e combinazione pesata → rimappatura in
  // [minScore, 10]. Solo se il Livello_Aggregato è presente. Il Livello è
  // normalizzato su base `0..5` (e-0→0 … e-5→1), coerente con
  // `levelExperienceToScore` del backend.
  const levelNorm =
    aggRank === null ? null : aggRank / (LIVELLO_VALUES.length - 1);
  const expNorm = (expRank - 1) / 4;
  const axisSum = axisWeights.level + axisWeights.experience;
  const raw =
    levelNorm === null
      ? null
      : axisSum === 0
        ? (levelNorm + expNorm) / 2
        : (axisWeights.level * levelNorm + axisWeights.experience * expNorm) / axisSum;
  // Livello "Nessuno" (aggregato a e-0, rango 0) ⇒ Score azzerato, come nel
  // backend; il pavimento `minScore` vale solo per aggregati `> 0`.
  const computed =
    raw === null ? null : aggRank !== null && aggRank <= 0 ? 0 : minScore + raw * (10 - minScore);

  // Bonus certificazioni: mostrato come passo aggiuntivo solo quando la skill ha
  // almeno una certificazione valida e la configurazione prevede un bonus > 0.
  // Il fattore moltiplicativo replica `applyCertificationBonus` del backend.
  const certificationCount = row.certificationCount;
  const { certificationBonus } = config;
  const hasCertificationBonus =
    computed !== null && certificationCount > 0 && certificationBonus > 0;
  const bonusFactor = 1 + (certificationCount * certificationBonus) / 100;
  const scoreWithBonus =
    computed === null ? null : hasCertificationBonus ? computed * bonusFactor : computed;

  const stop = (e: MouseEvent) => e.stopPropagation();

  return createPortal(
    <div onClick={stop} onMouseDown={stop}>
      <Modal
        open
        size="wide"
        onClose={onClose}
        title={`Come si calcola lo Score — ${row.skillName}`}
        footer={
          <Button type="button" variant="primary" onClick={onClose}>
            Ho capito
          </Button>
        }
      >
        {!hasLevel ? (
          <p className="livello-guide__intro">
            Questa skill non ha ancora alcuna <strong>Fonte di Livello</strong>{' '}
            (né Self, né AI, né voti di Reviewer o Manager), quindi lo Score non è
            calcolabile ed è mostrato come «—». Basta dichiarare il proprio
            Livello per iniziare a valorizzarlo.
          </p>
        ) : (
          <>
            <p className="livello-guide__intro">
              Lo Score deriva da due assi — <strong>Livello_Aggregato</strong> ed{' '}
              <strong>Esperienza</strong> — e viene rimappato nell'intervallo da{' '}
              <strong>{fmt(minScore)}</strong> a <strong>10</strong>
              {hasCertificationBonus ? (
                <>
                  , poi aumentato dal <strong>bonus certificazioni</strong>
                </>
              ) : null}
              . Ecco i dati di questa skill e i passaggi del calcolo.
            </p>

            {/* Passaggi del calcolo resi come timeline verticale: ogni step ha
                un marker numerato e il titolo a fianco; descrizione, tabelle e
                formule stanno nel corpo sotto al titolo. */}
            <ol className="score-timeline">
              {/* Passo 1: fonti di Livello presenti e loro contributo. */}
              <li className="score-timeline__step">
                <div className="score-timeline__head">
                  <span className="score-timeline__marker" aria-hidden="true">
                    1
                  </span>
                  <h3 className="score-timeline__title">Livello_Aggregato</h3>
                </div>
                <div className="score-timeline__body">
                  <p>
                    Media pesata dei livelli (1–5) delle sole fonti presenti:
                  </p>
                  <table className="score-detail__table">
                    <thead>
                      <tr>
                        <th scope="col">Fonte</th>
                        <th scope="col">Dato</th>
                        <th scope="col">Livello</th>
                        <th scope="col">Peso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributions.map((c) => (
                        <tr key={c.key}>
                          <th scope="row">{c.label}</th>
                          <td>{c.detail}</td>
                          <td className="score-detail__num">{fmt(c.rank)}</td>
                          <td className="score-detail__num">
                            {c.count > 1
                              ? `×${fmt(c.weightPerRater)} × ${c.count} = ×${fmt(c.weight)}`
                              : `×${fmt(c.weight)}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="score-detail__formula">
                    Livello_Aggregato ={' '}
                    {weightSum === 0 ? (
                      <>media semplice dei livelli = </>
                    ) : (
                      <>
                        (
                        {contributions
                          .map((c) => `${fmt(c.weight)}×${fmt(c.rank)}`)
                          .join(' + ')}
                        ) ÷ ({contributions.map((c) => fmt(c.weight)).join(' + ')}){' '}
                        ={' '}
                      </>
                    )}
                    <strong>{fmt(aggRank as number)}</strong> / 5
                  </p>
                </div>
              </li>

              {/* Passo 2: esperienza. */}
              <li className="score-timeline__step">
                <div className="score-timeline__head">
                  <span className="score-timeline__marker" aria-hidden="true">
                    2
                  </span>
                  <h3 className="score-timeline__title">Esperienza</h3>
                </div>
                <div className="score-timeline__body">
                  <p>
                    {row.yearsOfExperience === null ? (
                      <>
                        Non dichiarata, quindi trattata al valore minimo
                        (livello 1/5).
                      </>
                    ) : (
                      <>
                        {EXPERIENCE_RANGE_LABELS[row.yearsOfExperience]} →
                        livello {expRank}/5.
                      </>
                    )}
                  </p>
                </div>
              </li>

              {/* Passo 3: combinazione e rimappatura. */}
              <li className="score-timeline__step">
                <div className="score-timeline__head">
                  <span className="score-timeline__marker" aria-hidden="true">
                    3
                  </span>
                  <h3 className="score-timeline__title">Combinazione</h3>
                </div>
                <div className="score-timeline__body">
                  <p>
                    I due assi sono normalizzati in 0–1 (Livello peso{' '}
                    {fmt(axisWeights.level)}, Esperienza peso{' '}
                    {fmt(axisWeights.experience)}) e rimappati in {fmt(minScore)}
                    –10:
                  </p>
                  <p className="score-detail__formula">
                    Livello_norm = ({fmt(aggRank as number)} − 1) ÷ 4 ={' '}
                    {fmt(levelNorm as number)}
                    <br />
                    Esperienza_norm = ({expRank} − 1) ÷ 4 = {fmt(expNorm)}
                    <br />
                    Score = {fmt(minScore)} + {fmt(raw as number)} × (10 −{' '}
                    {fmt(minScore)}) = <strong>{fmt(computed as number)}</strong>
                  </p>
                </div>
              </li>

              {/* Passo 4 (solo con certificazioni valide): bonus certificazioni.
                  Modificatore moltiplicativo che può portare lo Score oltre 10. */}
              {hasCertificationBonus && (
                <li className="score-timeline__step">
                  <div className="score-timeline__head">
                    <span className="score-timeline__marker" aria-hidden="true">
                      4
                    </span>
                    <h3 className="score-timeline__title">Bonus certificazioni</h3>
                  </div>
                  <div className="score-timeline__body">
                    <p>
                      {certificationCount === 1 ? (
                        <>
                          Questa skill è coperta da <strong>1 certificazione</strong>{' '}
                          valida, che aggiunge un bonus del{' '}
                          <strong>{fmt(certificationBonus)}%</strong> allo Score.
                        </>
                      ) : (
                        <>
                          Questa skill è coperta da{' '}
                          <strong>{certificationCount} certificazioni</strong> valide:
                          ognuna aggiunge {fmt(certificationBonus)}%, per un bonus
                          totale del{' '}
                          <strong>{fmt(certificationCount * certificationBonus)}%</strong>{' '}
                          allo Score.
                        </>
                      )}
                    </p>
                    <p className="score-detail__formula">
                      Score = {fmt(computed as number)} × (1 +{' '}
                      {certificationCount} × {fmt(certificationBonus)}% ) ={' '}
                      <strong>{fmt(scoreWithBonus as number)}</strong>
                    </p>
                    <p className="score-detail__hint">
                      Il bonus può portare lo Score oltre 10: in tal caso è mostrato
                      come «10+».
                    </p>
                  </div>
                </li>
              )}

              {/* Passo finale: Score finale (valore autorevole dell'API). */}
              <li className="score-timeline__step">
                <div className="score-timeline__head">
                  <span className="score-timeline__marker" aria-hidden="true">
                    {hasCertificationBonus ? 5 : 4}
                  </span>
                  <h3 className="score-timeline__title">Score finale</h3>
                </div>
                <div className="score-timeline__body">
                  <p className="score-detail__result">
                    <strong>
                      {row.score === null
                        ? '—'
                        : row.score > 10
                          ? '10+'
                          : row.score.toFixed(1)}
                    </strong>
                    <span className="score-detail__result-note">
                      {row.score !== null && row.score > 10
                        ? `(${row.score.toFixed(1)}, oltre la scala ${fmt(minScore)}–10)`
                        : `(su scala ${fmt(minScore)}–10)`}
                    </span>
                  </p>
                </div>
              </li>
            </ol>
          </>
        )}
      </Modal>
    </div>,
    document.body,
  );
}

export default ScoreDetailModal;
